import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  LayoutDashboard, Calendar, Briefcase, Users, Megaphone, Clock,
  Mail, Heart, MessageSquare, Settings, LogOut, Menu, X, FileSpreadsheet, FileText,
  Megaphone as MegaphoneIcon, Image as ImageIcon, Tag, Building, BarChart3, HardDrive,
  BookOpenText,
} from "lucide-react";


const LINKS = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/services", label: "Services", icon: Briefcase },
  { to: "/admin/facility-rentals", label: "Facility Rentals", icon: Building },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/community-resources", label: "Community Resources", icon: BookOpenText },
  { to: "/admin/gallery", label: "Photo Gallery", icon: ImageIcon },
  { to: "/admin/imams", label: "Imams", icon: Users },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
  { to: "/admin/impact-stats", label: "Impact Stats", icon: BarChart3 },
  { to: "/admin/iqamah", label: "Iqamah Times", icon: Clock },
  { to: "/admin/prayer-card", label: "Prayer Card Banner", icon: MegaphoneIcon },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/donations", label: "Donations", icon: Heart },
  { to: "/admin/designations", label: "Donation Causes", icon: Heart },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare },
  { to: "/admin/contact-types", label: "Contact Request Types", icon: Tag },
  { to: "/admin/reports", label: "Weekly Report", icon: FileSpreadsheet },
  { to: "/admin/storage", label: "Storage Cleanup", icon: HardDrive },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
  { to: "/admin/team", label: "Team", icon: Users },
];

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => { if (!loading && !user) nav("/admin/login"); }, [user, loading, nav]);

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-ink/50">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-cream text-ink flex">
      <aside className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-emerald_deep text-cream flex-col transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"} lg:flex flex`}>
        <div className="p-6 border-b border-cream/10 flex items-center gap-3">
          <img src="/fic-logo.png" alt="FIC" className="h-10 bg-cream/95 p-1 rounded" />
          <div>
            <div className="font-serif text-lg leading-tight">FIC Admin</div>
            <div className="text-[10px] uppercase tracking-wider text-gold">Control room</div>
          </div>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 my-0.5 rounded-xl text-sm transition-all ${
                  isActive ? "bg-gold text-emerald_deep font-semibold" : "text-cream/75 hover:bg-cream/10 hover:text-cream"
                }`
              }
            >
              <l.icon size={16} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-cream/10 text-xs text-cream/60">
          <div className="truncate mb-2">{user.email}</div>
          <div className="flex gap-2">
            <Link to="/" className="flex-1 rounded-lg bg-cream/10 hover:bg-cream/15 px-3 py-2 text-center text-xs" data-testid="admin-view-site">View site</Link>
            <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout" className="flex-1 rounded-lg bg-gold/20 hover:bg-gold/30 text-gold px-3 py-2 text-xs inline-flex items-center justify-center gap-1.5">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur border-b border-sand px-6 py-4 flex items-center justify-between lg:hidden">
          <button onClick={() => setOpen((v) => !v)} className="p-2">{open ? <X size={20} /> : <Menu size={20} />}</button>
          <div className="font-serif">FIC Admin</div>
          <div className="w-8" />
        </header>
        <main className="p-6 md:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
